'use client';

import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { PromptBit, PromptBitCategory } from '@/lib/types';
import api from '@/lib/api';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmDialog';
import SearchPromptsButton from '../SearchPromptsButton';

interface BitLibraryPanelProps {
  categories: PromptBitCategory[];
  bits: PromptBit[];
  onRefresh: () => void;
  onInsertBit: (bit: PromptBit, mode: 'build' | 'swap') => void;
  onInsertInlineList: (categorySlug: string, texts: string[]) => void;
  /** Bits currently "live" in the prompt (per category) — ring-highlighted in swap mode */
  activeBitIds?: Set<number>;
  /** Cast a Person bit: append them to the prompt and let the agent fill the character around them */
  onCastBit?: (bit: PromptBit) => void;
}

const SWAP_MODE_STORAGE_KEY = 'builder-swap-mode';
const CATEGORY_STORAGE_KEY = 'builder-library-category';

// Scroll restore must run before paint in the browser; useLayoutEffect is a no-op warning
// during SSR, so fall back to useEffect on the server.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// Backend may not send tags yet (contract rolls out in parallel) — stay defensive at runtime
function bitTags(bit: PromptBit): string[] {
  return bit.tags ?? [];
}

/** Comma-separated input → normalized (lowercased, trimmed, deduped) tag list */
function parseTagsInput(value: string): string[] {
  const seen = new Set<string>();
  for (const part of value.split(',')) {
    const tag = part.trim().toLowerCase();
    if (tag) seen.add(tag);
  }
  return [...seen];
}

// Deterministic hue from a label for the no-preview thumbnail fallback
function labelHue(label: string): number {
  let h = 0;
  for (let i = 0; i < label.length; i++) h = ((h << 5) + h + label.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}

function excerpt(text: string): string {
  return text.length > 60 ? `${text.slice(0, 60)}…` : text;
}

export default function BitLibraryPanel({
  categories,
  bits,
  onRefresh,
  onInsertBit,
  onInsertInlineList,
  activeBitIds,
  onCastBit,
}: BitLibraryPanelProps) {
  // Search state
  const [search, setSearch] = useState('');

  // Two-column navigation: which category column B (the item grid) displays.
  // Hydrated from localStorage after mount; falls back to the first category.
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  // Below lg the panel shows one column at a time: 'nav' = category list, 'items' = bit grid
  const [mobileView, setMobileView] = useState<'nav' | 'items'>('nav');

  // Column B scroll memory (per category)
  const gridScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionsRef = useRef<Map<number, number>>(new Map());
  // Category the scroll container currently displays — keys the save-on-scroll
  const scrollCategoryRef = useRef<number | null>(null);

  // Tag filter state (per category, AND semantics) + '+n more' chip-row expansion
  const [tagFilters, setTagFilters] = useState<Map<number, Set<string>>>(new Map());

  // Swap mode: clicking a bit replaces its section instead of the build insert flow.
  // Initialized off and hydrated from localStorage in an effect to avoid SSR mismatch.
  const [swapMode, setSwapMode] = useState(false);

  // Inline editor state
  const [expandedBitId, setExpandedBitId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editText, setEditText] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editPrivate, setEditPrivate] = useState(false);
  const [isSavingBit, setIsSavingBit] = useState(false);

  // Variate state
  const [variateCount, setVariateCount] = useState(5);
  const [variateInstructions, setVariateInstructions] = useState('');
  const [variateCandidates, setVariateCandidates] = useState<string[] | null>(null);
  const [isVariating, setIsVariating] = useState(false);

  // AI-generate state (per category, one form open at a time)
  const [genCategoryId, setGenCategoryId] = useState<number | null>(null);
  const [genInstructions, setGenInstructions] = useState('');
  const [genCount, setGenCount] = useState(10);
  const [genCandidates, setGenCandidates] = useState<{ label: string; text: string; tags?: string[] }[] | null>(null);
  const [genChecked, setGenChecked] = useState<Set<number>>(new Set());
  // Per-candidate editable tags (comma-separated), prefilled from the AI suggestion
  const [genTagEdits, setGenTagEdits] = useState<Record<number, string>>({});
  const [isGeneratingBits, setIsGeneratingBits] = useState(false);
  const [isAddingBits, setIsAddingBits] = useState(false);

  // Preview generation state (one run at a time)
  const [previewCategoryId, setPreviewCategoryId] = useState<number | null>(null);
  const [previewProgress, setPreviewProgress] = useState<{ total: number; completed: number; current: string } | null>(null);
  const [previewMenuCategoryId, setPreviewMenuCategoryId] = useState<number | null>(null);
  const [previewCacheBuster, setPreviewCacheBuster] = useState<number>(Date.now());
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Consecutive failed status polls — tolerate transient errors before giving up
  const pollFailuresRef = useRef(0);

  // Multi-select state (one category at a time)
  const [selectCategoryId, setSelectCategoryId] = useState<number | null>(null);
  const [selectedBitIds, setSelectedBitIds] = useState<Set<number>>(new Set());

  // Add-bit state
  const [addCategoryId, setAddCategoryId] = useState<number | null>(null);
  const [addLabel, setAddLabel] = useState('');
  const [addText, setAddText] = useState('');
  const [addTags, setAddTags] = useState('');
  const [isAddingBit, setIsAddingBit] = useState(false);

  const toast = useToast();
  const { confirm } = useConfirm();

  // Clear poll interval on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Hydrate swap mode from localStorage after mount
  useEffect(() => {
    try {
      if (localStorage.getItem(SWAP_MODE_STORAGE_KEY) === 'true') setSwapMode(true);
    } catch {
      // localStorage unavailable (private mode etc.) — stay off
    }
  }, []);

  const toggleSwapMode = () => {
    setSwapMode(prev => {
      const next = !prev;
      try {
        localStorage.setItem(SWAP_MODE_STORAGE_KEY, String(next));
      } catch {
        // Non-persistent is fine
      }
      return next;
    });
  };

  const hasSearch = search.trim().length > 0;

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id),
    [categories]
  );

  // Hydrate / validate the selected category: keep the current one when it still exists,
  // else try the stored one, else fall back to the first category.
  useEffect(() => {
    if (sortedCategories.length === 0) return;
    setSelectedCategoryId(prev => {
      if (prev !== null && sortedCategories.some(c => c.id === prev)) return prev;
      try {
        const stored = localStorage.getItem(CATEGORY_STORAGE_KEY);
        if (stored !== null) {
          const id = Number(stored);
          if (Number.isInteger(id) && sortedCategories.some(c => c.id === id)) return id;
        }
      } catch {
        // localStorage unavailable — fall through to the first category
      }
      return sortedCategories[0].id;
    });
  }, [sortedCategories]);

  const selectCategoryRow = (categoryId: number, navigate: boolean) => {
    setSelectedCategoryId(categoryId);
    try {
      localStorage.setItem(CATEGORY_STORAGE_KEY, String(categoryId));
    } catch {
      // Non-persistent is fine
    }
    // Row clicks navigate the mobile drawer to the item grid; per-category action icons
    // keep the drawer on column A (their forms open there).
    if (navigate) setMobileView('items');
  };

  // Restore column B's remembered scroll position when the displayed category changes.
  // mobileView is a dep because below lg the grid is display:none until 'items' shows it —
  // scrollTop can only be applied once the element has a box.
  useIsomorphicLayoutEffect(() => {
    scrollCategoryRef.current = selectedCategoryId;
    if (selectedCategoryId === null) return;
    const el = gridScrollRef.current;
    if (!el) return;
    el.scrollTop = scrollPositionsRef.current.get(selectedCategoryId) ?? 0;
  }, [selectedCategoryId, mobileView]);

  const handleGridScroll = () => {
    const el = gridScrollRef.current;
    const categoryId = scrollCategoryRef.current;
    if (el && categoryId !== null) {
      scrollPositionsRef.current.set(categoryId, el.scrollTop);
    }
  };

  const filteredBits = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bits.filter(b => {
      if (q && !(b.label.toLowerCase().includes(q) || b.text.toLowerCase().includes(q))) {
        return false;
      }
      const selected = tagFilters.get(b.category_id);
      if (selected && selected.size > 0) {
        const tags = bitTags(b);
        for (const tag of selected) {
          if (!tags.includes(tag)) return false;
        }
      }
      return true;
    });
  }, [bits, search, tagFilters]);

  // Per category: unique tags sorted by frequency desc (ties alphabetically).
  // Derived from ALL bits of the category so the chip row stays stable while filtering.
  const tagsByCategory = useMemo(() => {
    const counts = new Map<number, Map<string, number>>();
    for (const bit of bits) {
      for (const tag of bitTags(bit)) {
        let catCounts = counts.get(bit.category_id);
        if (!catCounts) {
          catCounts = new Map();
          counts.set(bit.category_id, catCounts);
        }
        catCounts.set(tag, (catCounts.get(tag) || 0) + 1);
      }
    }
    const out = new Map<number, string[]>();
    for (const [categoryId, catCounts] of counts) {
      out.set(
        categoryId,
        [...catCounts.entries()]
          .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
          .map(([tag]) => tag)
      );
    }
    return out;
  }, [bits]);

  const toggleTagFilter = (categoryId: number, tag: string) => {
    setTagFilters(prev => {
      const next = new Map(prev);
      const current = new Set(next.get(categoryId) ?? []);
      if (current.has(tag)) current.delete(tag);
      else current.add(tag);
      if (current.size === 0) next.delete(categoryId);
      else next.set(categoryId, current);
      return next;
    });
  };

  const bitsByCategory = useMemo(() => {
    const map = new Map<number, PromptBit[]>();
    for (const bit of filteredBits) {
      const list = map.get(bit.category_id);
      if (list) list.push(bit);
      else map.set(bit.category_id, [bit]);
    }
    return map;
  }, [filteredBits]);

  const activeCounts = useMemo(() => {
    const map = new Map<number, number>();
    for (const bit of bits) {
      if (bit.is_active) map.set(bit.category_id, (map.get(bit.category_id) || 0) + 1);
    }
    return map;
  }, [bits]);

  const selectCategory = selectCategoryId !== null
    ? sortedCategories.find(c => c.id === selectCategoryId) || null
    : null;

  const getPreviewUrl = (previewImage: string) => {
    return `${api.getImageUrl(previewImage)}?t=${previewCacheBuster}`;
  };

  // === BIT CARDS ===

  const handleCardClick = (bit: PromptBit) => {
    if (selectCategoryId === bit.category_id) {
      setSelectedBitIds(prev => {
        const next = new Set(prev);
        if (next.has(bit.id)) next.delete(bit.id);
        else next.add(bit.id);
        return next;
      });
    } else {
      onInsertBit(bit, swapMode ? 'swap' : 'build');
    }
  };

  // === INLINE EDITOR ===

  const openEditor = (bit: PromptBit) => {
    if (expandedBitId === bit.id) {
      setExpandedBitId(null);
      return;
    }
    setExpandedBitId(bit.id);
    setEditLabel(bit.label);
    setEditText(bit.text);
    setEditNotes(bit.notes || '');
    setEditTags(bitTags(bit).join(', '));
    setEditActive(bit.is_active);
    setEditPrivate(bit.is_private);
    setVariateCandidates(null);
    setVariateInstructions('');
    setVariateCount(5);
  };

  const handleSaveBit = async (bit: PromptBit) => {
    if (!editLabel.trim() || !editText.trim()) return;

    setIsSavingBit(true);
    try {
      await api.updatePromptBit(bit.id, {
        label: editLabel.trim(),
        text: editText.trim(),
        // Explicit empty string (not undefined) so clearing notes actually persists —
        // an absent key is "no change" under the backend's exclude_unset PATCH
        notes: editNotes.trim(),
        is_active: editActive,
        is_private: editPrivate,
        tags: parseTagsInput(editTags),
      });
      toast.success('Bit updated');
      onRefresh();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setIsSavingBit(false);
    }
  };

  const handleDeleteBit = async (bit: PromptBit) => {
    const confirmed = await confirm({
      title: 'Delete Bit',
      message: `Delete "${bit.label}"? This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await api.deletePromptBit(bit.id);
      toast.success('Bit deleted');
      setExpandedBitId(null);
      onRefresh();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  // === VARIATE ===

  const handleVariate = async (bit: PromptBit) => {
    setIsVariating(true);
    try {
      const response = await api.aiVariateBit({
        bit_id: bit.id,
        count: Math.min(20, Math.max(1, variateCount)),
        instructions: variateInstructions.trim() || undefined,
      });
      setVariateCandidates(response.variations);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setIsVariating(false);
    }
  };

  const handleAddVariant = async (bit: PromptBit, index: number) => {
    const text = variateCandidates?.[index];
    if (!text) return;

    try {
      await api.createPromptBit({
        category_id: bit.category_id,
        label: `${bit.label} var`,
        text,
        is_private: bit.is_private,
      });
      setVariateCandidates(prev => prev ? prev.filter((_, i) => i !== index) : prev);
      toast.success('Variation added as new bit');
      onRefresh();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  // === AI GENERATE (per category) ===

  const toggleGenForm = (categoryId: number) => {
    setGenCandidates(null);
    setGenChecked(new Set());
    setGenTagEdits({});
    setGenInstructions('');
    setGenCount(10);
    setGenCategoryId(prev => prev === categoryId ? null : categoryId);
  };

  const handleGenerateBits = async (category: PromptBitCategory) => {
    setIsGeneratingBits(true);
    try {
      const response = await api.aiGenerateBits({
        category_id: category.id,
        instructions: genInstructions.trim() || undefined,
        count: genCount,
      });
      setGenCandidates(response.bits);
      setGenChecked(new Set(response.bits.map((_, i) => i)));
      setGenTagEdits(Object.fromEntries(
        response.bits.map((candidate, i) => [i, (candidate.tags ?? []).join(', ')])
      ));
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setIsGeneratingBits(false);
    }
  };

  const toggleGenChecked = (index: number) => {
    setGenChecked(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleAddGeneratedBits = async (category: PromptBitCategory) => {
    if (!genCandidates) return;
    const chosenIndices = [...genChecked].sort((a, b) => a - b).filter(i => genCandidates[i] !== undefined);
    if (chosenIndices.length === 0) return;

    setIsAddingBits(true);
    const succeeded = new Set<number>();
    let failed = false;
    try {
      for (const i of chosenIndices) {
        const candidate = genCandidates[i];
        await api.createPromptBit({
          category_id: category.id,
          label: candidate.label,
          text: candidate.text,
          tags: parseTagsInput(genTagEdits[i] ?? (candidate.tags ?? []).join(',')),
        });
        succeeded.add(i);
      }
    } catch (err) {
      failed = true;
      toast.error((err as Error).message);
    } finally {
      setIsAddingBits(false);
    }

    if (succeeded.size > 0) {
      toast.success(`Added ${succeeded.size} bit${succeeded.size !== 1 ? 's' : ''}`);
      onRefresh();
    }
    if (!failed) {
      setGenCandidates(null);
      setGenChecked(new Set());
      setGenTagEdits({});
      setGenCategoryId(null);
    } else if (succeeded.size > 0) {
      // Keep the form open with the not-yet-added candidates so the user can retry;
      // reindex the checked set and tag edits to match the filtered list
      const remainingChecked = new Set<number>();
      const remainingTagEdits: Record<number, string> = {};
      let newIndex = 0;
      genCandidates.forEach((_, i) => {
        if (succeeded.has(i)) return;
        if (genChecked.has(i)) remainingChecked.add(newIndex);
        const tagEdit = genTagEdits[i];
        if (tagEdit !== undefined) remainingTagEdits[newIndex] = tagEdit;
        newIndex++;
      });
      setGenCandidates(genCandidates.filter((_, i) => !succeeded.has(i)));
      setGenChecked(remainingChecked);
      setGenTagEdits(remainingTagEdits);
    }
  };

  // === PREVIEW GENERATION ===

  const stopPreviewPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPreviewCategoryId(null);
    setPreviewProgress(null);
  };

  const handleGeneratePreviews = async (category: PromptBitCategory, missingOnly: boolean) => {
    if (previewCategoryId !== null) return;
    setPreviewMenuCategoryId(null);

    if (!category.preview_template) {
      toast.error(`Set a preview template on "${category.label}" first. Use {value} as a placeholder.`);
      return;
    }

    setPreviewCategoryId(category.id);
    setPreviewProgress({ total: 0, completed: 0, current: '' });

    try {
      const result = await api.generateBitPreviews(category.id, missingOnly);
      toast.success(result.message || 'Preview generation started');

      pollFailuresRef.current = 0;
      pollRef.current = setInterval(async () => {
        try {
          const status = await api.getBitPreviewStatus(category.id);
          pollFailuresRef.current = 0;

          if (status.status === 'in_progress') {
            setPreviewProgress({
              total: status.total || 0,
              completed: status.completed || 0,
              current: status.current || '',
            });
          } else if (status.status === 'completed' || status.status === 'idle') {
            stopPreviewPolling();
            setPreviewCacheBuster(Date.now());
            onRefresh();
            toast.success('Preview generation completed');
          } else if (status.status === 'cancelled') {
            stopPreviewPolling();
            setPreviewCacheBuster(Date.now());
            onRefresh();
            toast.success('Preview generation cancelled');
          } else if (status.status === 'error') {
            stopPreviewPolling();
            toast.error(status.message || 'Preview generation failed');
          }
        } catch {
          // Skip isolated failed ticks (transient network blips) — only give up after
          // several consecutive failures, and say so instead of silently resetting
          pollFailuresRef.current += 1;
          if (pollFailuresRef.current >= 3) {
            stopPreviewPolling();
            toast.error('Lost connection while tracking preview generation');
          }
        }
      }, 2000);
    } catch (err) {
      stopPreviewPolling();
      toast.error((err as Error).message);
    }
  };

  const handleCancelPreviews = async (categoryId: number) => {
    try {
      await api.cancelBitPreviews(categoryId);
      toast.success('Cancellation requested');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  // === MULTI-SELECT ===

  const toggleSelectMode = (categoryId: number) => {
    setSelectedBitIds(new Set());
    setSelectCategoryId(prev => prev === categoryId ? null : categoryId);
  };

  const handleInsertInline = (category: PromptBitCategory) => {
    const texts = bits
      .filter(b => b.category_id === category.id && selectedBitIds.has(b.id))
      .map(b => b.text);
    if (texts.length === 0) return;
    onInsertInlineList(category.slug, texts);
    setSelectCategoryId(null);
    setSelectedBitIds(new Set());
  };

  // === ADD BIT ===

  const openAddForm = (categoryId: number) => {
    if (addCategoryId === categoryId) {
      setAddCategoryId(null);
      return;
    }
    setAddCategoryId(categoryId);
    setAddLabel('');
    setAddText('');
    setAddTags('');
  };

  const handleAddBit = async (category: PromptBitCategory) => {
    if (!addLabel.trim() || !addText.trim()) return;

    setIsAddingBit(true);
    try {
      await api.createPromptBit({
        category_id: category.id,
        label: addLabel.trim(),
        text: addText.trim(),
        tags: parseTagsInput(addTags),
      });
      setAddLabel('');
      setAddText('');
      setAddTags('');
      toast.success('Bit added');
      onRefresh();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setIsAddingBit(false);
    }
  };

  // === DERIVED (two-column layout) ===

  const selectedCategory = selectedCategoryId !== null
    ? sortedCategories.find(c => c.id === selectedCategoryId) ?? null
    : null;
  const selectedCatBits = selectedCategory ? bitsByCategory.get(selectedCategory.id) ?? [] : [];
  const selectedCategoryTags = selectedCategory ? tagsByCategory.get(selectedCategory.id) ?? [] : [];
  const selectedTagFilter = selectedCategory ? tagFilters.get(selectedCategory.id) : undefined;
  const visibleTags = selectedCategoryTags;
  // Multi-select mode active for the category column B currently shows
  const isSelectMode = selectedCategory !== null && selectCategoryId === selectedCategory.id;

  // Categories the per-category function UIs (opened from column A rows) target
  const genCategory = genCategoryId !== null
    ? sortedCategories.find(c => c.id === genCategoryId) ?? null
    : null;
  const addCategory = addCategoryId !== null
    ? sortedCategories.find(c => c.id === addCategoryId) ?? null
    : null;
  const previewMenuCategory = previewMenuCategoryId !== null
    ? sortedCategories.find(c => c.id === previewMenuCategoryId) ?? null
    : null;
  const previewRunningCategory = previewCategoryId !== null
    ? sortedCategories.find(c => c.id === previewCategoryId) ?? null
    : null;

  return (
    <div className="h-full flex overflow-hidden">
      {/* ===== Column A: navigation + functions ===== */}
      <div className={`${mobileView === 'items' ? 'hidden lg:flex' : 'flex'} flex-col w-full lg:w-80 flex-shrink-0 min-h-0`}>
        {/* Search + swap mode */}
        <div className="p-3 border-b border-[var(--border-subtle)] flex-shrink-0 flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-muted)]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search bits..."
              className="w-full pl-8 pr-8 py-1.5 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none text-sm"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-pointer"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <button
            onClick={toggleSwapMode}
            title="Swap mode: clicking a bit replaces its section directly"
            className={`px-2.5 py-1 rounded-full text-[10px] font-medium border transition-colors cursor-pointer flex-shrink-0 ${
              swapMode
                ? 'bg-[var(--accent-soft)] text-[var(--accent)] border-[var(--accent)]'
                : 'text-[var(--text-muted)] border-[var(--border-subtle)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
            }`}
          >
            Swap
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {sortedCategories.length === 0 ? (
            <div className="flex items-center justify-center h-24 text-sm text-[var(--text-muted)]">
              No categories yet
            </div>
          ) : (
            <>
              {/* Category list */}
              <div className="p-2 space-y-0.5">
                {sortedCategories.map(category => {
                  const matchCount = (bitsByCategory.get(category.id) || []).length;
                  const isRowSelected = selectedCategoryId === category.id;
                  const isPreviewRunning = previewCategoryId === category.id;
                  return (
                    <div
                      key={category.id}
                      className={`flex items-center gap-1 px-2 py-1.5 rounded-lg transition-colors ${
                        isRowSelected ? 'bg-[var(--accent-soft)]' : 'hover:bg-[var(--bg-hover)]'
                      } ${hasSearch && matchCount === 0 ? 'opacity-40' : ''}`}
                    >
                      <button
                        onClick={() => selectCategoryRow(category.id, true)}
                        className="flex items-center gap-1.5 flex-1 min-w-0 text-left cursor-pointer group"
                        title={`Show ${category.label} bits`}
                      >
                        <span className={`text-sm font-medium truncate transition-colors ${
                          isRowSelected ? 'text-[var(--accent)]' : 'group-hover:text-[var(--accent)]'
                        }`}>
                          {category.label}
                        </span>
                        <span
                          className="text-xs text-[var(--text-muted)] flex-shrink-0"
                          title={hasSearch ? 'Matching bits' : 'Active bits'}
                        >
                          {hasSearch ? matchCount : (activeCounts.get(category.id) || 0)}
                        </span>
                      </button>
                      <button
                        onClick={() => {
                          selectCategoryRow(category.id, false);
                          toggleGenForm(category.id);
                        }}
                        className={`p-1 rounded transition-colors cursor-pointer ${
                          genCategoryId === category.id
                            ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
                            : 'text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-hover)]'
                        }`}
                        title="AI-generate bits for this category"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          selectCategoryRow(category.id, false);
                          if (!isPreviewRunning) {
                            setPreviewMenuCategoryId(prev => prev === category.id ? null : category.id);
                          }
                        }}
                        disabled={previewCategoryId !== null && !isPreviewRunning}
                        className={`p-1 rounded transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
                          isPreviewRunning || previewMenuCategoryId === category.id
                            ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
                            : 'text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-hover)]'
                        }`}
                        title="Generate preview images"
                      >
                        {isPreviewRunning ? (
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          selectCategoryRow(category.id, false);
                          toggleSelectMode(category.id);
                        }}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors cursor-pointer ${
                          selectCategoryId === category.id
                            ? 'bg-[var(--accent)] text-black'
                            : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
                        }`}
                        title="Select multiple bits to insert as an inline list"
                      >
                        select
                      </button>
                      <button
                        onClick={() => {
                          selectCategoryRow(category.id, false);
                          openAddForm(category.id);
                        }}
                        className={`p-1 rounded transition-colors cursor-pointer ${
                          addCategoryId === category.id
                            ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
                            : 'text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-hover)]'
                        }`}
                        title="Add a bit to this category"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Selected category's tag filter chips (multi-select, AND) */}
              {selectedCategory && selectedCategoryTags.length > 0 && (
                <div className="px-3 py-2 border-t border-[var(--border-subtle)]">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1.5">
                    {selectedCategory.label} tags
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {visibleTags.map(tag => {
                      const isActiveTag = selectedTagFilter?.has(tag) ?? false;
                      return (
                        <button
                          key={tag}
                          onClick={() => toggleTagFilter(selectedCategory.id, tag)}
                          className={`px-1.5 py-0.5 rounded-full text-[10px] transition-colors cursor-pointer ${
                            isActiveTag
                              ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
                              : 'bg-[var(--bg-elevated)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                          }`}
                          title={isActiveTag ? `Remove tag filter "${tag}"` : `Filter by tag "${tag}"`}
                        >
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Preview scope choice */}
              {previewMenuCategory && previewCategoryId === null && (
                <div className="px-3 py-2 border-t border-[var(--border-subtle)] flex items-center gap-2 text-xs">
                  <span className="text-[var(--text-muted)] truncate">{previewMenuCategory.label} previews:</span>
                  <button
                    onClick={() => handleGeneratePreviews(previewMenuCategory, true)}
                    className="text-[var(--accent)] hover:underline cursor-pointer flex-shrink-0"
                    title="Generate previews only for bits that don't have one"
                  >
                    Missing only
                  </button>
                  <span className="text-[var(--text-muted)]">|</span>
                  <button
                    onClick={() => handleGeneratePreviews(previewMenuCategory, false)}
                    className="text-[var(--accent)] hover:underline cursor-pointer flex-shrink-0"
                    title="Regenerate all preview images"
                  >
                    All bits
                  </button>
                </div>
              )}

              {/* Preview progress */}
              {previewRunningCategory && previewProgress && (
                <div className="px-3 py-2 border-t border-[var(--border-subtle)]">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">
                    {previewRunningCategory.label} previews
                  </div>
                  <div className="flex items-center justify-between text-xs text-[var(--text-muted)] mb-1 gap-2">
                    <span className="truncate">Generating: {previewProgress.current || '...'}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span>{previewProgress.completed} / {previewProgress.total}</span>
                      <button
                        onClick={() => handleCancelPreviews(previewRunningCategory.id)}
                        className="text-red-400 hover:underline cursor-pointer"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                  <div className="w-full h-1.5 bg-[var(--bg-surface)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[var(--accent)] transition-all duration-300"
                      style={{ width: previewProgress.total > 0 ? `${(previewProgress.completed / previewProgress.total) * 100}%` : '0%' }}
                    />
                  </div>
                </div>
              )}

              {/* AI-generate form */}
              {genCategory && (
                <div className="px-3 py-3 border-t border-[var(--border-subtle)] space-y-2">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                    AI-generate — {genCategory.label}
                  </div>
                  <textarea
                    value={genInstructions}
                    onChange={(e) => setGenInstructions(e.target.value)}
                    className="input text-sm resize-none"
                    rows={2}
                    placeholder={`Instructions for new ${genCategory.label} bits (optional)...`}
                  />
                  <div className="flex items-center gap-2">
                    <select
                      value={genCount}
                      onChange={(e) => setGenCount(Number(e.target.value))}
                      className="input text-sm w-20 py-1.5"
                    >
                      {[5, 10, 20].map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleGenerateBits(genCategory)}
                      disabled={isGeneratingBits}
                      className="btn btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5"
                    >
                      {isGeneratingBits && (
                        <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      )}
                      {isGeneratingBits ? 'Generating...' : 'Generate'}
                    </button>
                    <button
                      onClick={() => toggleGenForm(genCategory.id)}
                      className="btn btn-ghost text-xs py-1.5 px-3"
                    >
                      Cancel
                    </button>
                  </div>
                  {genCandidates && (
                    <div className="space-y-1.5">
                      {genCandidates.map((candidate, i) => (
                        <label
                          key={i}
                          className="flex items-start gap-2 p-2 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)] cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={genChecked.has(i)}
                            onChange={() => toggleGenChecked(i)}
                            className="mt-0.5 accent-[var(--accent)] flex-shrink-0"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block text-xs font-medium">{candidate.label}</span>
                            <span className="block text-xs text-[var(--text-muted)]">{candidate.text}</span>
                            <input
                              type="text"
                              value={genTagEdits[i] ?? ''}
                              onChange={(e) => setGenTagEdits(prev => ({ ...prev, [i]: e.target.value }))}
                              onClick={(e) => e.stopPropagation()}
                              className="input mt-1 w-full text-[10px] py-0.5 px-1.5"
                              placeholder="tags, comma-separated"
                              title="Tags for this bit (comma-separated)"
                            />
                          </span>
                        </label>
                      ))}
                      <button
                        onClick={() => handleAddGeneratedBits(genCategory)}
                        disabled={genChecked.size === 0 || isAddingBits}
                        className="btn btn-primary w-full text-xs py-1.5"
                      >
                        {isAddingBits
                          ? 'Adding...'
                          : `Add ${genChecked.size} bit${genChecked.size !== 1 ? 's' : ''}`}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Add bit form */}
              {addCategory && (
                <div className="px-3 py-3 border-t border-[var(--border-subtle)] space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                    Add bit — {addCategory.label}
                  </div>
                  <input
                    type="text"
                    value={addLabel}
                    onChange={(e) => setAddLabel(e.target.value)}
                    className="input text-sm"
                    placeholder="Label"
                    autoFocus
                  />
                  <textarea
                    value={addText}
                    onChange={(e) => setAddText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleAddBit(addCategory);
                      }
                    }}
                    className="input text-sm resize-none"
                    rows={2}
                    placeholder="Prompt text..."
                  />
                  <input
                    type="text"
                    value={addTags}
                    onChange={(e) => setAddTags(e.target.value)}
                    className="input text-sm"
                    placeholder="Tags (comma-separated, optional)"
                    title="Tags, comma-separated — normalized to lowercase on save"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAddBit(addCategory)}
                      disabled={isAddingBit || !addLabel.trim() || !addText.trim()}
                      className="btn btn-primary text-xs py-1 px-3"
                    >
                      {isAddingBit ? 'Adding...' : 'Add'}
                    </button>
                    <button
                      onClick={() => setAddCategoryId(null)}
                      className="btn btn-ghost text-xs py-1 px-3"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ===== Column B: items ===== */}
      <div className={`${mobileView === 'items' ? 'flex' : 'hidden lg:flex'} flex-col flex-1 min-w-0 min-h-0 lg:border-l border-[var(--border-subtle)]`}>
        {/* Header: back (mobile drawer only) + selected category + filtered count */}
        <div className="px-3 py-2.5 border-b border-[var(--border-subtle)] flex-shrink-0 flex items-center gap-1.5">
          <button
            onClick={() => setMobileView('nav')}
            className="lg:hidden p-1 -ml-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
            title="Back to categories"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </button>
          <span className="text-sm font-medium truncate flex-1 min-w-0">
            {selectedCategory ? selectedCategory.label : 'Bits'}
          </span>
          {selectedCategory && (
            <span
              className="text-xs text-[var(--text-muted)] flex-shrink-0"
              title="Bits shown (after search and tag filters)"
            >
              {selectedCatBits.length}
            </span>
          )}
        </div>

        {/* Bit grid — scroll position remembered per category */}
        <div
          ref={gridScrollRef}
          onScroll={handleGridScroll}
          className="flex-1 overflow-y-auto min-h-0 p-3"
        >
          {!selectedCategory ? (
            <p className="text-xs text-[var(--text-muted)] py-1">Select a category</p>
          ) : selectedCatBits.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] py-1">
              {hasSearch
                ? 'No bits match your search'
                : (selectedTagFilter?.size ?? 0) > 0
                  ? 'No bits match the selected tags'
                  : 'No bits yet'}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {selectedCatBits.map(bit => {
                if (bit.id === expandedBitId) {
                  // Inline editor (spans both columns)
                  return (
                    <div
                      key={bit.id}
                      className="col-span-2 rounded-lg border border-[var(--accent)] bg-[var(--bg-elevated)] p-2.5 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-[var(--text-secondary)]">Edit bit</span>
                        <button
                          onClick={() => setExpandedBitId(null)}
                          className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-pointer"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <input
                        type="text"
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        className="input text-sm"
                        placeholder="Label"
                      />
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSaveBit(bit);
                          }
                        }}
                        className="input text-sm resize-y min-h-[60px]"
                        rows={3}
                        placeholder="Prompt text"
                      />
                      <input
                        type="text"
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        className="input text-sm"
                        placeholder="Notes (optional)"
                      />
                      <input
                        type="text"
                        value={editTags}
                        onChange={(e) => setEditTags(e.target.value)}
                        className="input text-sm"
                        placeholder="Tags (comma-separated)"
                        title="Tags, comma-separated — normalized to lowercase on save"
                      />
                      <div className="flex items-center gap-4 text-xs">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editActive}
                            onChange={(e) => setEditActive(e.target.checked)}
                            className="accent-[var(--accent)]"
                          />
                          Active
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editPrivate}
                            onChange={(e) => setEditPrivate(e.target.checked)}
                            className="accent-[var(--accent)]"
                          />
                          Private
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveBit(bit)}
                          disabled={isSavingBit || !editLabel.trim() || !editText.trim()}
                          className="btn btn-primary text-xs py-1 px-3 flex-1"
                        >
                          {isSavingBit ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => handleDeleteBit(bit)}
                          className="px-3 py-1 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors cursor-pointer text-xs font-medium"
                        >
                          Delete
                        </button>
                      </div>

                      {/* Variate */}
                      <div className="pt-2 border-t border-[var(--border-subtle)] space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={1}
                            max={20}
                            value={variateCount}
                            onChange={(e) => setVariateCount(parseInt(e.target.value, 10) || 5)}
                            className="input text-sm w-14 py-1"
                            title="Number of variations"
                          />
                          <input
                            type="text"
                            value={variateInstructions}
                            onChange={(e) => setVariateInstructions(e.target.value)}
                            className="input text-sm flex-1 py-1"
                            placeholder="Variate instructions (optional)..."
                          />
                          <button
                            onClick={() => handleVariate(bit)}
                            disabled={isVariating}
                            className="btn btn-ghost text-xs py-1 px-2 flex items-center gap-1"
                            title="Generate variations of this bit's text"
                          >
                            {isVariating && (
                              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                            )}
                            {isVariating ? 'Variating...' : 'Variate'}
                          </button>
                        </div>
                        {variateCandidates && variateCandidates.length > 0 && (
                          <div className="space-y-1.5">
                            {variateCandidates.map((candidate, i) => (
                              <div
                                key={i}
                                className="p-2 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)]"
                              >
                                <p className="text-xs mb-1.5">{candidate}</p>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleAddVariant(bit, i)}
                                    className="text-[10px] text-[var(--accent)] hover:underline cursor-pointer"
                                  >
                                    Add as new bit
                                  </button>
                                  <button
                                    onClick={() => setEditText(candidate)}
                                    className="text-[10px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline cursor-pointer"
                                  >
                                    Replace text
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                const isSelected = selectedBitIds.has(bit.id);
                const isActiveInPrompt = swapMode && (activeBitIds?.has(bit.id) ?? false);
                return (
                  <div
                    key={bit.id}
                    className={`relative group rounded-lg border-2 overflow-hidden transition-all ${
                      isSelectMode && isSelected
                        ? 'bg-[var(--accent-soft)] border-[var(--accent)]'
                        : bit.is_active
                          ? 'bg-[var(--bg-primary)] border-[var(--border-subtle)] hover:border-[var(--accent)]'
                          : 'bg-[var(--bg-surface)] border-[var(--border-subtle)] opacity-60 hover:opacity-80'
                    } ${isActiveInPrompt ? 'ring-1 ring-[var(--accent)]' : ''}`}
                    title={isActiveInPrompt ? 'Currently in the prompt' : undefined}
                  >
                    {!isSelectMode && (
                      <SearchPromptsButton
                        text={bit.text}
                        className="absolute top-1.5 right-1.5 z-20 p-1 rounded-md bg-black/60 text-white/80 hover:bg-[var(--accent)] hover:text-black opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
                        title="Find images whose prompt contains this bit"
                      />
                    )}
                    <button
                      onClick={() => handleCardClick(bit)}
                      className="w-full text-left cursor-pointer"
                      title={isSelectMode ? 'Toggle selection' : `Insert "${bit.label}"`}
                    >
                      <div className="aspect-square bg-[var(--bg-elevated)] relative overflow-hidden">
                        {bit.preview_image ? (
                          <img
                            src={getPreviewUrl(bit.preview_image)}
                            alt={bit.label}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            loading="lazy"
                          />
                        ) : (
                          <div
                            className="w-full h-full flex items-center justify-center text-xl font-semibold text-white/80"
                            style={{ backgroundColor: `hsl(${labelHue(bit.label)}, 45%, 28%)` }}
                          >
                            {bit.label.charAt(0).toUpperCase()}
                          </div>
                        )}

                        {isSelectMode && (
                          <div className={`absolute top-1 left-1 w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                            isSelected
                              ? 'bg-[var(--accent)] text-black'
                              : 'bg-black/50 text-white border-2 border-white/50'
                          }`}>
                            {isSelected && (
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        )}

                        {bit.is_private && (
                          <div className="absolute bottom-1 left-1 p-0.5 rounded bg-black/60 text-white/90" title="Private">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="px-1.5 py-1">
                        <div className="text-xs font-medium truncate">{bit.label}</div>
                        <div className="text-[10px] text-[var(--text-muted)] truncate" title={bit.text}>
                          {excerpt(bit.text)}
                        </div>
                        {bitTags(bit).length > 0 && (
                          <div className="flex flex-wrap gap-0.5 mt-0.5">
                            {bitTags(bit).slice(0, 3).map(tag => (
                              <span
                                key={tag}
                                className="px-1 rounded bg-[var(--bg-elevated)] text-[9px] leading-3.5 text-[var(--text-muted)]"
                              >
                                {tag}
                              </span>
                            ))}
                            {bitTags(bit).length > 3 && (
                              <span
                                className="text-[9px] leading-3.5 text-[var(--text-muted)]"
                                title={bitTags(bit).slice(3).join(', ')}
                              >
                                +{bitTags(bit).length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                    {onCastBit && selectedCategory.slug === 'person' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onCastBit(bit);
                        }}
                        className="absolute top-1 right-7 px-1.5 py-0.5 rounded bg-black/50 text-[10px] font-medium text-white/80 hover:text-white hover:bg-black/70 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                        title="Cast this person: add them to the prompt and let the agent fill hair, clothing, expression and pose"
                      >
                        Cast
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditor(bit);
                      }}
                      className="absolute top-1 right-1 p-0.5 rounded bg-black/50 text-white/70 hover:text-white hover:bg-black/70 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                      title="Edit bit"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 12h.008v.008H6.75V12zm5.25 0h.008v.008H12V12zm5.25 0h.008v.008h-.008V12z" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          )}

        </div>

        {/* Multi-select footer */}
        {selectCategory && (
          <div className="p-3 border-t border-[var(--border-subtle)] bg-[var(--bg-elevated)] flex items-center justify-between gap-2 flex-shrink-0">
            <span className="text-xs text-[var(--text-secondary)] truncate">
              {selectedBitIds.size === 0
                ? `Click ${selectCategory.label} bits to select`
                : `${selectedBitIds.size} selected`}
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => toggleSelectMode(selectCategory.id)}
                className="px-2 py-1.5 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-lg hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => handleInsertInline(selectCategory)}
                disabled={selectedBitIds.size === 0}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                  selectedBitIds.size === 0
                    ? 'bg-[var(--bg-hover)] text-[var(--text-muted)] cursor-not-allowed'
                    : 'bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)]'
                }`}
              >
                {'Insert as {a|b|c}'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
